import {
	Link,
	useLoaderData,
	useLocation,
	type ClientLoaderFunctionArgs,
} from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/common/errorBoundary'
import {
	getUser,
	getUserAttestations,
	getUserAttestationsCount,
	mapNFTNameToMetadataEntries,
} from '#app/server/db.server.ts'
import {
	TypographyDisplay,
	TypographyParagraph,
} from '../components/typography'
import { Button } from '../components/buttons'
import { defer, MetaFunction } from '@remix-run/node'
import generateUserProfileMeta from '#app/utils/meta/userProfileMeta.ts'
import ArtistProfile from '#app/components/artistsProfile/artistProfile.tsx'
import { getSigningContextForSigningUI } from '#app/utils/misc.server'

export const handle = {
	scripts: () => [{ src: 'https://www.instagram.com/embed.js', preload: true }],
}

export const meta: MetaFunction<typeof loader> = ({
	data,
	params,
	location,
}) => {
	const user = data?.user
	const origin = data?.origin
	const { pathname } = location
	// @ts-ignore
	const metaPayload = generateUserProfileMeta({ origin, path: pathname, user })
	return metaPayload
}

export const loader = async ({
	params,
	context,
	request,
}: ClientLoaderFunctionArgs) => {
	const url = new URL(request.url)
	try {
		if (!params.userId) {
			return {
				user: undefined,
				stats: {
					totalCollections: 0,
					totalProofOfExhibitions: 0,
					totalAttestations: 0,
				},
				attestations: [],
				origin: url.origin,
			}
		}

		let user

		const dynamicUser = await getUser(params.userId)
		const signModalParams = await getSigningContextForSigningUI(request)

		if (!dynamicUser || dynamicUser.error || !dynamicUser.username) {
			throw new Response('User not found', { status: 404 })
		}

		//if (dynamicUser.role && dynamicUser.role === 'artist') {
		user = {
			id: dynamicUser.id,
			username: dynamicUser.username,
			userAvatar:
				dynamicUser.metadata && dynamicUser.metadata.avatar
					? dynamicUser.metadata.avatar
					: null,
			wallets: dynamicUser.wallets,
			shortDescription: dynamicUser.shortDescription,
			longDescription: dynamicUser.longDescription,
			location: dynamicUser.location,
			socials: dynamicUser.socials,
			role: dynamicUser.role,
		}

		const collectionAttestations = await getUserAttestationsCount(
			user.id,
			false,
			'collection',
		)
		const proofOfExhibitionAttestations = await getUserAttestationsCount(
			user.id,
			false,
			'proofOfExhibition',
		)
		const totalAttestations = await getUserAttestationsCount(user.id, false)
		const includeTemplates =
			'collection,proofOfExhibition,comment,tweet,media,cast,mediaCitation,offchainSale,countersign,rollup'
		const attestationsPromise = getUserAttestations(
			user.id,
			1000,
			0,
			'DESC',
			false,
			includeTemplates,
		).then(attestations =>
			mapNFTNameToMetadataEntries(attestations.attestations).then(
				() => attestations.attestations,
			),
		)

		const stats = {
			totalCollections: collectionAttestations.count,
			totalProofOfExhibitions: proofOfExhibitionAttestations.count,
			totalAttestations: totalAttestations.count,
		}

		return defer({
			user,
			stats,
			attestations: attestationsPromise,
			origin: url.origin,
			signModalParams,
		})
	} catch (e) {
		console.error(e)
		if ((e as Response).status === 404)
			throw new Response('User not found', { status: 404 })
	}
}

export default function userPage() {
	const { user, stats, attestations, origin, signModalParams } =
		useLoaderData<typeof loader>()
	return (
		<ArtistProfile
			artist={user}
			stats={stats}
			attestations={attestations}
			origin={origin}
			signModalParams={signModalParams}
		/>
	)
}

export function ErrorBoundary() {
	const location = useLocation()
	const segments = location.pathname.split('/')
	const username = segments[segments.length - 1]

	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<div className="mx-auto flex h-full flex-grow flex-col justify-center bg-light text-textPrimaryLight dark:bg-dark dark:text-textPrimaryDark">
						<div className="mx-auto max-w-[1440px] px-6 py-16 sm:px-6 md:px-12 lg:px-24">
							<TypographyDisplay additionalClasses="mb-4">
								User profile Not Found
							</TypographyDisplay>
							<TypographyParagraph>
								We can't seem to find this user profile:
							</TypographyParagraph>
							<TypographyParagraph additionalClasses="whitespace-pre-wrap break-all text-body-lg mb-8">
								{username || location.pathname}
							</TypographyParagraph>

							<Link to="/" prefetch="none">
								<Button variant="primary">Go to Homepage</Button>
							</Link>
						</div>
					</div>
				),
			}}
		/>
	)
}
